import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Box, Typography, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import FolderOffRoundedIcon from '@mui/icons-material/FolderOffRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useQueryClient } from '@tanstack/react-query';
import { Folder, createFolder, updateFolder } from '../../services/folders_api';
import { folderCache } from '../../services/folders_cache';
import { FOLDER_COLORS, FOLDER_ICONS, FolderGlyph, folderColor, DEFAULT_FOLDER_COLOR } from './folderVisuals';
import { useUser } from '../../contexts/UserContext';
import { getFolderMaxCount, FOLDER_COUNT_LIMITS, FOLDER_ITEM_LIMITS, isSubscribed } from '../../utils/subscription';
import PlanUpgradeCTA from '../common/PlanUpgradeCTA';

interface FolderEditDialogProps {
  open: boolean;
  onClose: () => void;
  folder: Folder | null; // null = 新規作成
  onSaved?: () => void;
  /** 現在のフォルダ総数（新規作成時の上限判定に使う） */
  folderCount?: number;
}

const NAME_MAX = 50;

// フォルダの作成・編集フォーム。
const FolderEditDialog: React.FC<FolderEditDialogProps> = ({ open, onClose, folder, onSaved, folderCount = 0 }) => {
  const isEdit = !!folder;
  const queryClient = useQueryClient();
  const { user } = useUser();
  const plan = user?.subscription_plan ?? 0;
  const maxFolders = getFolderMaxCount(plan);
  // 新規作成かつ上限到達時は、フォームの代わりに誘導パネルを出す。
  const atLimit = !isEdit && folderCount >= maxFolders;
  // 新規作成かつ無料プランは、お試し枠をフォーム内で案内する。
  const showTrialHint = !isEdit && !atLimit && !isSubscribed(plan);
  const [name, setName] = useState('');
  const [color, setColor] = useState('');
  const [icon, setIcon] = useState('');
  const [visibility, setVisibility] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(folder?.name ?? '');
      setColor(folder?.color ?? '');
      setIcon(folder?.icon ?? '');
      setVisibility(folder?.visibility ?? 1);
      setError(null);
    }
  }, [open, folder]);

  const previewColor = folderColor(color);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('フォルダ名を入力してください。');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = { name: trimmed, color, icon, visibility };
      if (isEdit) {
        const updated = await updateFolder(folder!.id, payload);
        folderCache.update(queryClient, updated);
      } else {
        const created = await createFolder(payload);
        folderCache.add(queryClient, created);
      }
      onSaved?.();
      onClose();
    } catch (e: any) {
      const detail = e?.response?.data;
      setError(
        (Array.isArray(detail?.name) && detail.name[0]) ||
          detail?.detail ||
          (typeof detail === 'string' ? detail : null) ||
          'フォルダの保存に失敗しました。',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>
        {isEdit ? 'フォルダを編集' : 'フォルダを作成'}
      </DialogTitle>
      <DialogContent>
        {atLimit ? (
          <Box sx={{ textAlign: 'center', py: 1.5 }}>
            <FolderOffRoundedIcon sx={{ fontSize: '2.4rem', color: 'rgba(255,255,255,0.25)', mb: 1 }} />
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 0.5 }}>
              フォルダの上限に達しました
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 2 }}>
              現在のプランで作成できるフォルダは {maxFolders} 個までです（{folderCount}/{maxFolders}）。
            </Typography>
            <PlanUpgradeCTA
              currentPlan={plan}
              currentValue={folderCount}
              limits={FOLDER_COUNT_LIMITS}
              unit="個"
              verb="作れ"
              showMultiplier={false}
            />
          </Box>
        ) : (
          <>
        {/* 無料プランのお試し枠を案内 */}
        {showTrialHint && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1,
              py: 0.85,
              mb: 2,
              borderRadius: '8px',
              backgroundColor: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: '0.95rem', color: 'text.secondary', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.5 }}>
              無料プランではフォルダ {FOLDER_COUNT_LIMITS.free} 個・各フォルダ {FOLDER_ITEM_LIMITS.free} 件までお試しいただけます
            </Typography>
          </Box>
        )}

        {/* プレビュー */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mb: 2,
            px: 1.2,
            py: 0.85,
            borderRadius: '999px',
            border: '1px solid',
            borderColor: `${previewColor}55`,
            backgroundColor: `${previewColor}1f`,
            alignSelf: 'flex-start',
            width: 'fit-content',
            maxWidth: '100%',
          }}
        >
          <FolderGlyph iconKey={icon} sx={{ fontSize: '1.05rem', color: previewColor }} />
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {name.trim() || 'フォルダ名'}
          </Typography>
        </Box>

        <TextField
          autoFocus
          fullWidth
          size="small"
          label="フォルダ名"
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, NAME_MAX))}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
          helperText={`${name.length}/${NAME_MAX}`}
          FormHelperTextProps={{ sx: { textAlign: 'right', m: 0, mt: 0.25 } }}
          sx={{ mb: 2 }}
        />

        {/* 色 */}
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 0.75, fontWeight: 600 }}>
          カラー
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.85, mb: 2 }}>
          {FOLDER_COLORS.map((c) => {
            const active = (color || DEFAULT_FOLDER_COLOR) === c;
            return (
              <Box
                key={c}
                onClick={() => setColor(c)}
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  backgroundColor: c,
                  cursor: 'pointer',
                  boxShadow: active ? `0 0 0 2px #1a1a1a, 0 0 0 4px ${c}` : 'none',
                  transition: 'transform .12s ease',
                  '&:hover': { transform: 'scale(1.15)' },
                }}
              />
            );
          })}
        </Box>

        {/* アイコン */}
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 0.75, fontWeight: 600 }}>
          アイコン
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 2 }}>
          {FOLDER_ICONS.map(({ key, label, Icon }) => {
            const active = (icon || '') === key;
            return (
              <Box
                key={key || 'default'}
                onClick={() => setIcon(key)}
                title={label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 34,
                  height: 34,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: active ? `${previewColor}88` : 'rgba(255,255,255,0.08)',
                  backgroundColor: active ? `${previewColor}1f` : 'rgba(255,255,255,0.03)',
                  color: active ? previewColor : 'rgba(255,255,255,0.5)',
                  transition: 'all .12s ease',
                  '&:hover': { borderColor: `${previewColor}66` },
                }}
              >
                <Icon sx={{ fontSize: '1.1rem' }} />
              </Box>
            );
          })}
        </Box>

        {/* 公開設定 */}
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 0.75, fontWeight: 600 }}>
          公開設定
        </Typography>
        <ToggleButtonGroup
          value={visibility}
          exclusive
          fullWidth
          size="small"
          onChange={(_e, v) => { if (v !== null) setVisibility(v); }}
        >
          <ToggleButton value={1} sx={{ textTransform: 'none', gap: 0.5, fontSize: '0.78rem' }}>
            <PublicRoundedIcon sx={{ fontSize: '0.95rem' }} /> 公開
          </ToggleButton>
          <ToggleButton value={0} sx={{ textTransform: 'none', gap: 0.5, fontSize: '0.78rem' }}>
            <LockRoundedIcon sx={{ fontSize: '0.95rem' }} /> 非公開
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.35)', mt: 0.75 }}>
          {visibility === 1
            ? '公開プロフィールのフォルダバーに表示されます。'
            : '自分だけが見られます。'}
        </Typography>

        {error && (
          <Typography sx={{ fontSize: '0.75rem', color: 'error.main', mt: 1.5 }}>{error}</Typography>
        )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
          {atLimit ? '閉じる' : 'キャンセル'}
        </Button>
        {!atLimit && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !name.trim()}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {isEdit ? '保存' : '作成'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FolderEditDialog;
